import { Chip, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SmartPalette from '../../generic/smartPalette/SmartPalette'
import WorkflowResourceTypes from './resourcetypes/WorkflowResourcesTypes'
import { getJobAssignmentsFor, computeAssignmentCompletionStatus, ASSIGNMENT_STATUS, normalizeBomPosition, parentBomPosition } from './bomTree'
import { collectAssemblyNodeIds } from './assemblyHighlight'

/**
 * @typedef {import('./bomTree').JobAssignment} JobAssignment
 */

/**
 * S28/S29/S30 (RN09.1-RN09.3): renders one chip per job assigned to a BOM component (tooltip with
 * job name + quantity), or an explicit "Not assigned" chip when there is none.
 * @param {Object} props
 * @param {import('./bomTree').JobAssignment[]} props.jobAssignments
 * @param {function(import('./bomTree').JobAssignment)} props.onHighlightJob
 */
const BomJobChips = ({ jobAssignments = [], onHighlightJob, t_ }) => {
  if (jobAssignments.length === 0) {
    // S30 (RN09.3): explicit "not assigned" indication, never a silently empty row.
    return <Chip size='small' variant='outlined' label={t_('Not assigned')} />
  }

  return (
    <>
      {jobAssignments.map((assignment) => (
        <Tooltip
          key={assignment.jobNodeId}
          title={`${assignment.jobName ? assignment.jobName + ' ' : ''}(${assignment.quantity})`}
          placement='top'
          arrow
        >
          <Chip
            size='small'
            color='primary'
            label={assignment.jobCode}
            onClick={(event) => {
              // S31/S32 (RN09.4/D5): only requests a highlight, never navigates/selects by itself.
              event.stopPropagation()
              onHighlightJob(assignment)
            }}
          />
        </Tooltip>
      ))}
    </>
  )
}

/**
 * S25/S27 (RN08.1/RN08.3) + enmienda PO 23/07/2026: the default BOM view REUSES the same tree the
 * Resources section shows when a JOB node is selected with a BOM loaded (`WorkflowResourceTypes`
 * inside a `SmartPalette`, which brings the search box), in read-only mode (no drag) and extended
 * with the per-job chips (HU-09) and the parent "fully assigned" indicator (HU-11).
 * @param {Object} props
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource[]} props.items BOM components (flat list, position-based hierarchy)
 * @param {Object<string, JobAssignment[]>} props.jobAssignmentsByUUID Job assignments per workflowResourceUUID
 * @param {function(JobAssignment, string)} props.onHighlightJob Called when a job chip is clicked
 * (S31/S32), with the assignment and the `uuid` of the component whose chip it was
 * @param {string} props.highlightedResourceUUID S31/S32 (ajuste PO 2026-07-27): the component whose
 * chip produced the current graph highlight — marked in the same blue as the highlighted node, so
 * the row the highlight came from is identifiable in the tree
 * @param {function()} props.onClearHighlight Ajuste PO 2026-07-27: called when a click lands outside
 * this view, so the selection can be dropped
 * @param {boolean} [props.showJobChips=true] Historia 117: the BOM library shows the same tree for a
 * library version, where there are no jobs to assign — the per-row chips are switched off there
 * @param {boolean} [props.visibleSearch=true] Historia 117: hides the palette's own search box when
 * the host already filters the items (the BOM library's "Search component" field)
 * @param {function(Set<string>, string)} [props.onHighlightAssembly] S69/S70 (historia 152,
 * RN-02.6): called when an ASSEMBLY row (one with children) is selected, with the set of graph
 * node ids that consume any of its components — at any depth, already collapsed into the node
 * each job shows up as on the current canvas (E8) — and the assembly's own `uuid`. Pure reading:
 * the view asks for a highlight and nothing else (RN-02.7)
 * @returns
 */
const BomTreeView = ({ items, jobAssignmentsByUUID = {}, onHighlightJob = () => {}, highlightedResourceUUID = '', onClearHighlight = () => {}, showJobChips = true, visibleSearch = true, onHighlightAssembly = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const viewRef = useRef(null)
  // S69/S70: the assembly selected in THIS view and how many nodes its highlight reached. Local
  // state, exactly like the chip highlight: nothing of HU-02 reaches the editor's model (IN14).
  const [selectedAssembly, setSelectedAssembly] = useState({ uuid: '', consumerCount: 0 })

  const itemsWithAssignments = useMemo(() => {
    return (items || []).map((item) => ({
      ...item,
      jobAssignments: getJobAssignmentsFor(item.uuid, jobAssignmentsByUUID)
    }))
  }, [items, jobAssignmentsByUUID])

  // S69 (RN-02.6): which rows ARE assemblies — the ones some other component hangs from, by the
  // very normalized-position rule the tree builds its hierarchy with. A leaf is not an assembly
  // and selecting it highlights nothing.
  const assemblyPositions = useMemo(() => {
    const positions = new Set((items || []).map((item) => normalizeBomPosition(item.position)))
    const assemblies = new Set()
    positions.forEach((position) => {
      const parent = parentBomPosition(position)
      if (parent && positions.has(parent)) {
        assemblies.add(parent)
      }
    })
    return assemblies
  }, [items])

  const clearAssemblySelection = useCallback(() => {
    setSelectedAssembly({ uuid: '', consumerCount: 0 })
    onClearHighlight()
  }, [onClearHighlight])

  // S69/S70/S71: selecting an assembly row resolves its operations with E8 and reports them. It
  // never selects a node, never navigates and never writes; selecting it again switches it off.
  const onItemClick = useCallback((event, itemId) => {
    const item = (items || []).find((candidate) => candidate.position + '-' + candidate.uuid === itemId)
    if (!item || !assemblyPositions.has(normalizeBomPosition(item.position))) {
      return
    }
    if (selectedAssembly.uuid === item.uuid) {
      clearAssemblySelection()
      return
    }
    const nodeIds = collectAssemblyNodeIds({ assemblyPosition: item.position, components: items, assignmentsByUUID: jobAssignmentsByUUID })
    setSelectedAssembly({ uuid: item.uuid, consumerCount: nodeIds.size })
    onHighlightAssembly(nodeIds, item.uuid)
  }, [items, assemblyPositions, jobAssignmentsByUUID, selectedAssembly.uuid, clearAssemblySelection, onHighlightAssembly])

  const sections = useMemo(() => [{
    id: 'BOM',
    type: 'BOM',
    title: t_('BOM'),
    items: itemsWithAssignments,
    defaultExpanded: true,
    customRender: (section, sectionItems) => (
      <div data-testid='bom-tree-view'>
        <WorkflowResourceTypes
          key='bom-view-tree'
          section={section}
          items={sectionItems}
          getDragProperties={() => false}
          readOnly
          onItemClick={onItemClick}
          isItemCompleted={(item) => computeAssignmentCompletionStatus(item) === ASSIGNMENT_STATUS.COMPLETE}
          // S69: the assembly whose highlight is on is marked in the very same blue as the row a
          // chip highlight came from — one channel, one color, two ways in.
          isItemHighlighted={(item) => (!!highlightedResourceUUID && item.uuid === highlightedResourceUUID) || (!!selectedAssembly.uuid && item.uuid === selectedAssembly.uuid)}
          renderItemFooter={showJobChips
            ? (item) => (
              <BomJobChips
                jobAssignments={item.jobAssignments || []}
                // The chip only knows its job; the row it belongs to is what the tree has to mark.
                onHighlightJob={(assignment) => onHighlightJob(assignment, item.uuid)}
                t_={t_}
              />
              )
            : undefined}
        />
      </div>
    )
  }], [itemsWithAssignments, onHighlightJob, highlightedResourceUUID, showJobChips, onItemClick, selectedAssembly.uuid, t_])

  // Ajuste PO 2026-07-27: a click landing anywhere outside this view drops the selection. Listening
  // on `mousedown` (not `click`) keeps the row's own chips working: their `mousedown` target is
  // inside the view, so selecting one chip right after another never clears in between. The
  // listener only exists while something is selected — with nothing to clear there is nothing to
  // watch for. The search box counts as part of the view: using it is not "clicking outside".
  // S71 (RN-02.7): the assembly highlight switches off by the same door — clicking outside drops
  // it and the graph goes back to exactly the presentation it had.
  useEffect(() => {
    if (!highlightedResourceUUID && !selectedAssembly.uuid) {
      return
    }
    const clearOnOutsideClick = (event) => {
      if (viewRef.current?.contains(event.target)) {
        return
      }
      setSelectedAssembly({ uuid: '', consumerCount: 0 })
      onClearHighlight()
    }
    document.addEventListener('mousedown', clearOnOutsideClick)
    return () => document.removeEventListener('mousedown', clearOnOutsideClick)
  }, [highlightedResourceUUID, selectedAssembly.uuid, onClearHighlight])

  if (!itemsWithAssignments || itemsWithAssignments.length === 0) {
    return null
  }

  return (
    <div ref={viewRef}>
      {/* S70 (RN-02.8, J35): an assembly none of whose components is consumed highlights zero
          nodes — and says so, instead of staying silent or lighting the whole graph up. */}
      {selectedAssembly.uuid && selectedAssembly.consumerCount === 0
        ? (
          <Typography variant='caption' color='text.secondary' data-testid='assembly-without-consumers'>
            {t_('No operations consume this assembly')}
          </Typography>
          )
        : null}
      <SmartPalette
        sections={sections}
        // Same code+name search semantics as the Resources section palette (ResourceTypesBar).
        getLabel={(section, item) => (item?.resource?.name || '') + ' (' + (item?.resource?.code || '') + ')'}
        searchByCodeName
        visibleSearch={visibleSearch}
      />
    </div>
  )
}

export default BomTreeView
export { BomJobChips }
